'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { formatInUserTimezone } from '@/lib/timezone-utils';
import { generateGoogleCalendarUrl, generateOutlookUrl, generateYahooUrl, downloadIcsFile } from '@/lib/calendar-utils';
import { sendGAEvent } from '@next/third-parties/google';
import { usePostHog } from 'posthog-js/react';
import { CommunityEvent } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar, Clock, MapPin, ChevronDown, Users, CheckCircle2 } from 'lucide-react';
import { clientLogger } from '@/lib/logger-client';
import EventRegistrationModal from './EventRegistrationModal';

interface EventCardProps {
  event: CommunityEvent;
}

export default function EventCard({ event }: EventCardProps) {
  const { data: session } = useSession();
  const posthog = usePostHog();
  const [userTimezone, setUserTimezone] = useState<string>(() => 
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [registrantCount, setRegistrantCount] = useState<number>(0);
  const [isRegistered, setIsRegistered] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await fetch(`/api/events/stats?eventId=${event.id}`);
      if (res.ok) {
        const data = await res.json();
        setRegistrantCount(data.registrantCount || 0);
      }
    } catch (err) {
      clientLogger.warn('[EventCard] Failed to fetch stats', { err });
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function loadStatsAndUserStatus() {
      try {
        const res = await fetch(`/api/events/stats?eventId=${event.id}`);
        if (res.ok && isMounted) {
          const data = await res.json();
          setRegistrantCount(data.registrantCount || 0);
        }
      } catch (err) {
        clientLogger.warn('[EventCard] Failed to fetch stats', { err });
      }

      if (session?.user?.id) {
        try {
          const userRes = await fetch('/api/user/registered-events');
          if (userRes.ok && isMounted) {
            const userData = await userRes.json();
            if (userData.events?.some((e: { id: string }) => e.id === event.id)) {
              setIsRegistered(true);
            }
          }
        } catch (err) {
          clientLogger.warn('[EventCard] Failed to check user registration status', { err });
        }
      }
    }
    loadStatsAndUserStatus();
    return () => {
      isMounted = false;
    };
  }, [event.id, session?.user?.id]);



  const [isUnregistering, setIsUnregistering] = useState(false);

  const handleUnregister = async () => {
    setIsUnregistering(true);
    try {
      const res = await fetch('/api/events/unregister', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: event.id }),
      });
      if (res.ok) {
        setIsRegistered(false);
        const statsRes = await fetch(`/api/events/stats?eventId=${event.id}`);
        if (statsRes.ok) {
          const data = await statsRes.json();
          setRegistrantCount(data.registrantCount || 0);
        }

        // Trigger immediate notification sync to recalculate invitations
        if (session?.user?.id) {
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
          fetch('/api/notifications/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userTimezone }),
          }).catch(() => {});
        }
      }
    } catch (err) {
      clientLogger.warn('[EventCard] Failed to unregister', { err });
    } finally {
      setIsUnregistering(false);
    }
  };

  const isPast = Boolean(event.isPast);

  const eventStart = formatInUserTimezone(event.eventDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a', userTimezone);
  const eventEnd = formatInUserTimezone(
    new Date(event.eventDate.getTime() + event.durationMinutes * 60000),
    'h:mm a',
    userTimezone
  );

  const timezoneLabel = userTimezone === 'America/New_York' ? 'ET' :
                       userTimezone.split('/').pop()?.replace('_', ' ') || userTimezone;

  const handleAddToCalendar = (platform: 'google' | 'outlook' | 'yahoo' | 'apple' | 'ics') => {
    const calendarEvent = {
      id: event.id,
      title: event.title,
      description: event.description,
      startDate: event.eventDate,
      endDate: new Date(event.eventDate.getTime() + event.durationMinutes * 60000),
      location: event.meetingLink,
      url: event.meetingLink,
    };

    // Track the calendar addition event
    try {
      sendGAEvent('event', 'event_calendar_add', {
        event_id: event.id,
        event_title: event.title,
        calendar_platform: platform,
        user_timezone: userTimezone,
        is_authenticated: !!session?.user?.id,
        user_id: session?.user?.id,
      });

      if (posthog) {
        posthog.capture('event_calendar_add', {
          event_id: event.id,
          event_title: event.title,
          calendar_platform: platform,
          user_timezone: userTimezone,
          is_authenticated: !!session?.user?.id,
          distinct_id: session?.user?.id,
        });
      }
    } catch (error) {
      clientLogger.warn('[EventCard] Analytics tracking failed:', { error });
    }

    switch (platform) {
      case 'google':
        window.open(generateGoogleCalendarUrl(calendarEvent), '_blank');
        break;
      case 'outlook':
        window.open(generateOutlookUrl(calendarEvent), '_blank');
        break;
      case 'yahoo':
        window.open(generateYahooUrl(calendarEvent), '_blank');
        break;
      case 'apple':
        downloadIcsFile(calendarEvent);
        break;
      case 'ics':
        downloadIcsFile(calendarEvent);
        break;
    }
  };

  return (
    <>
      <Card className="h-full flex flex-col justify-between">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="secondary" className="capitalize text-xs">
                  {event.eventType}
                </Badge>

                {isPast && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800 font-medium">
                    Past Event
                  </Badge>
                )}

                {/* Only display registrant count IF greater than 10 */}
                {registrantCount > 10 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {registrantCount} registered
                  </Badge>
                )}
              </div>

              <CardTitle className="text-xl mb-2">{event.title}</CardTitle>
              <CardDescription className="text-base">{event.description}</CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Event Details */}
          <div className="space-y-2">
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{eventStart} - {eventEnd} {timezoneLabel}</span>
            </div>

            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="w-4 h-4 mr-2" />
              <span>{event.durationMinutes} minutes</span>
            </div>

            <div className="flex items-center text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 mr-2" />
              <span className="font-medium text-foreground">Online</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            {isPast ? (
              <Button
                disabled
                className="w-full rounded-full bg-muted text-muted-foreground font-medium py-2.5 cursor-not-allowed"
              >
                Event Ended
              </Button>
            ) : !isRegistered ? (
              <Button
                onClick={() => setIsModalOpen(true)}
                className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-xs py-2.5"
              >
                Register for Event
              </Button>
            ) : (
              <>
                {session?.user?.id ? (
                  <Button
                    variant="outline"
                    onClick={handleUnregister}
                    disabled={isUnregistering}
                    className="w-full border-slate-300 text-slate-700 hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-red-950/40 dark:hover:text-red-400 rounded-full transition-colors"
                  >
                    {isUnregistering ? 'Unregistering...' : 'Unregister'}
                  </Button>
                ) : (
                  <Button
                    disabled
                    variant="outline"
                    className="w-full border-emerald-300 bg-emerald-50/50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300 rounded-full cursor-default"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-600 dark:text-emerald-400" />
                    Registered
                  </Button>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto rounded-full">
                      Add to Calendar
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleAddToCalendar('google')}>
                      Google Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAddToCalendar('outlook')}>
                      Outlook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAddToCalendar('yahoo')}>
                      Yahoo Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAddToCalendar('apple')}>
                      Apple Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAddToCalendar('ics')}>
                      Download ICS File
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {isRegistered && !session?.user?.id && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              Check your confirmation email to manage or cancel your RSVP.
            </p>
          )}

        </CardContent>
      </Card>

      <EventRegistrationModal
        event={event}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRegisteredSuccess={() => {
          setIsRegistered(true);
          fetchStats();
        }}
      />
    </>
  );
}